import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import UserAvatar from "../components/UserAvatar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Star, Download, ShoppingBag, Check, Loader2 } from "lucide-react";
import api from "../lib/api";

export default function MarketplaceItemPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [purchasing, setPurchasing] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/marketplace/items/${id}`)
      .then((r) => setItem(r.data))
      .catch(() => navigate("/marketplace"))
      .finally(() => setLoading(false));
    api.get(`/marketplace/items/${id}/reviews`)
      .then((r) => setReviews(r.data.reviews))
      .catch(() => {});
  }, [id, navigate]);

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      await api.post(`/marketplace/items/${id}/purchase`);
      toast.success(item.is_free ? "Downloaded!" : "Purchased!");
      const r = await api.get(`/marketplace/items/${id}`);
      setItem(r.data);
    } catch { toast.error("Failed"); }
    finally { setPurchasing(false); }
  };

  const handleReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    try {
      await api.post(`/marketplace/items/${id}/review`, { rating: reviewRating, review_text: reviewText });
      toast.success("Review submitted!");
      setReviewText("");
      const r = await api.get(`/marketplace/items/${id}/reviews`);
      setReviews(r.data.reviews);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSubmittingReview(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;
  if (!item) return null;

  const canView = item.is_purchased || item.is_owner || item.is_free;

  return (
    <div className="min-h-screen" data-testid="marketplace-item-page">
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate("/marketplace")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="font-outfit text-sm font-semibold text-foreground truncate">{item.title}</h1>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
          <div className="h-40 bg-gradient-to-br from-cyan-500/15 to-violet-500/20 flex items-center justify-center">
            <ShoppingBag className="w-16 h-16 text-cyan-300" />
          </div>
          <div className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h1 className="font-outfit text-xl font-semibold text-foreground mb-1" data-testid="item-title">{item.title}</h1>
                <span className="text-xs text-muted-foreground capitalize">{item.content_type?.replace(/_/g, " ")} &middot; {item.category}</span>
              </div>
              <span className={`text-lg font-bold ${item.is_free ? "text-emerald-400" : "text-cyan-400"}`}>
                {item.is_free ? "Free" : `$${item.price?.toFixed(2)}`}
              </span>
            </div>
            <Link to={`/profile/${item.seller_username}`} className="flex items-center gap-2 no-underline mb-4">
              <UserAvatar name={item.seller_display_name} username={item.seller_username} size="sm" />
              <div>
                <p className="text-xs font-semibold text-foreground">{item.seller_display_name}</p>
                <p className="text-[10px] text-muted-foreground/70">@{item.seller_username}</p>
              </div>
            </Link>
            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
              {item.rating_count > 0 && <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" /> {item.rating_avg} ({item.rating_count})</span>}
              <span className="flex items-center gap-1"><Download className="w-3.5 h-3.5" /> {item.downloads_count} downloads</span>
            </div>
            {!canView && (
              <Button onClick={handlePurchase} disabled={purchasing} className="w-full bg-gradient-to-r from-cyan-400 to-violet-500 text-white h-11 rounded-lg press-down" data-testid="purchase-btn">
                {purchasing ? "Processing..." : item.is_free ? "Get for Free" : `Purchase — $${item.price?.toFixed(2)}`}
              </Button>
            )}
            {item.is_purchased && <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium" data-testid="purchased-badge"><Check className="w-4 h-4" /> Purchased</div>}
          </div>
        </div>

        {/* Description */}
        <div className="bg-card border border-border/60 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-foreground mb-2">Description</h2>
          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{item.description}</p>
        </div>

        {/* Preview */}
        <div className="bg-card border border-border/60 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-foreground mb-2">Preview</h2>
          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{item.preview_content}</p>
        </div>

        {/* Full Content (if accessible) */}
        {canView && item.full_content && (
          <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-5" data-testid="full-content">
            <h2 className="text-sm font-semibold text-cyan-200 mb-2">Full Content</h2>
            <p className="text-sm text-cyan-200 leading-relaxed whitespace-pre-wrap">{item.full_content}</p>
          </div>
        )}

        {/* Reviews */}
        <div className="bg-card border border-border/60 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-foreground mb-3">Reviews ({reviews.length})</h2>
          {reviews.length === 0 ? (
            <p className="text-xs text-muted-foreground/70 text-center py-4">No reviews yet</p>
          ) : (
            <div className="space-y-3 mb-4">
              {reviews.map((r) => (
                <div key={r.id} className="flex gap-3">
                  <UserAvatar name={r.buyer_display_name} username={r.buyer_username} size="sm" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-foreground">{r.buyer_display_name}</span>
                      <div className="flex gap-0.5">{[1,2,3,4,5].map((s) => <Star key={s} className={`w-3 h-3 ${s <= r.rating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/50"}`} />)}</div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{r.review_text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {item.is_purchased && (
            <form onSubmit={handleReview} className="border-t border-border/60 pt-3">
              <div className="flex gap-1 mb-2">{[1,2,3,4,5].map((s) => <button key={s} type="button" onClick={() => setReviewRating(s)} className={`bg-transparent border-0 cursor-pointer p-0`}><Star className={`w-5 h-5 ${s <= reviewRating ? "text-amber-400 fill-amber-400" : "text-muted-foreground/50"}`} /></button>)}</div>
              <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Write your review..." rows={2} className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 resize-none mb-2" data-testid="review-input" />
              <Button type="submit" disabled={submittingReview} className="bg-indigo-500 text-white text-xs h-8 px-4 rounded-lg" data-testid="submit-review-btn">{submittingReview ? "..." : "Submit Review"}</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
